import React from 'react';
import bemify from 'bemify-js';

import { StaticImage } from '@zapier/common-components';

import Code from '../components/Code';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';
import SyntaxHighlighter from '../components/SyntaxHighlighter';

const bem = bemify('create-action');

const jsCodeBlock = `const sample = require('../samples/sample_issue');

const createIssue = (z, bundle) => {
  const responsePromise = z.request({
    method: 'POST',
    // Defining the interaction with your API endpoint. The bundle
    // parameter holds data input and authentication information
    // from the Zap:
    url: \`https://api.github.com/repos/\${bundle.inputData.repo}/issues\`,
    body: JSON.stringify({
    title: bundle.inputData.title
    })
  });
  return responsePromise
    .then(response => JSON.parse(response.content));
};

module.exports = {
  key: 'issue',
  noun: 'Issue',

display: {
  // What the user will see in the Zap Editor when selecting an action
  label: 'Create Issue',
  description: 'Creates an issue.'
},

operation: {
   // Data users will be asked to set in the Zap Editor
  inputFields: [
      {key: 'repo', label: 'Repo', required: true,
      // Creates a dynamic drop-down that's populated by the 'repo' trigger
      dynamic: 'repo.full_name.full_name'},
      {key: 'title', label:'Title', required: true},
      {key: 'body', label:'Body', required: false}
    ],
    perform: createIssue,
    // Sample data that the user will see if they skip the test
    // step in the Zap Editor
    sample: sample
  }
}`;

const CreateAction = () => (
  <div className={bem()}>
    <StepTitle>Explore the Project: A Create Action</StepTitle>
    <Paragraph>
      You'll notice a couple of folders called <Code>/creates</Code> and{' '}
      <Code>/triggers</Code>. Unsurprisingly, these are where the code for your
      create actions, and triggers live.
    </Paragraph>
    <Paragraph>
      "Creates" are how you get data from a Zap to your API - think{' '}
      <Code>POST</Code> or <Code>PUT</Code>
      HTTP actions.
    </Paragraph>
    <Paragraph>
      "Triggers" are where you get data from your API, <Code>GET</Code> HTTP
      actions or web hooks.
    </Paragraph>
    <Paragraph>
      The code here makes calls to your API endpoint and tells the Zapier
      platform how to create the Zap Editor form where users can configure their
      Zaps and map data to and from your API.
    </Paragraph>
    <Paragraph>
      A quick look at our example create code in <Code>creates/issue.js</Code>:
    </Paragraph>
    <SyntaxHighlighter language="javascript">{jsCodeBlock}</SyntaxHighlighter>
    <Paragraph>
      And here's an example of what this will look like when a user configures
      it in the Zap Editor:
    </Paragraph>
    <StaticImage
      alt="create action screenshot"
      src="https://cdn.zappy.app/a24892690780d13b48bb219485cb766e.png"
      title="create action screenshot"
    />
  </div>
);

export default CreateAction;
